/**
 * La matemática de los conectores: dónde se engancha una arista, por dónde
 * pasa y qué tan lejos cae un click de ella.
 *
 * Todo acá es **puro**: no importa Pixi ni toca el DOM. Es a propósito — es la
 * única parte del render que se puede ejercitar sin navegador, y es donde vive
 * la lógica que más fácil se rompe en silencio.
 *
 * La ruta se **calcula**, nunca se guarda (ver `Edge` en `model/types.ts`): de
 * ahí sale gratis que la flecha siga a sus nodos al moverlos, redimensionarlos
 * o arrastrarlos dentro de un contenedor.
 */

import { type EdgeRoute, type EdgeSide } from "../model/types.ts";

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Tramo recto que sale perpendicular al borde antes de doblar, en px de mundo.
 * Es lo que hace que la flecha "salga derecha" del nodo en vez de nacer en
 * diagonal, y lo que evita que la línea pase raspando la esquina.
 */
const STUB = 18;

/** Largo de las tangentes de una ruta curva, acotado para que no se desboque. */
const CURVE_MIN = 40;
const CURVE_MAX = 160;
/** El tope se afloja cuando el otro extremo queda detrás: con el tope normal,
 *  la curva no llegaba a rodear y terminaba cortando por encima del nodo. */
const CURVE_MAX_BACK = CURVE_MAX * 2;

/** Segmentos con los que se muestrea una curva. */
const CURVE_SAMPLES = 32;

/**
 * Cuánto puede salirse una ruta del rectángulo que envuelve a sus dos nodos.
 * Se usa para descartar aristas fuera de pantalla SIN calcular su ruta: la caja
 * union+margen es un superconjunto del camino, así que nada visible se pierde.
 */
export const ROUTE_MARGIN = CURVE_MAX_BACK + CURVE_MAX + STUB;

/** Vector unitario que apunta hacia afuera del nodo desde cada lado. */
const SIDE_NORMAL: Record<EdgeSide, Point> = {
  top: { x: 0, y: -1 },
  right: { x: 1, y: 0 },
  bottom: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
};

function isHorizontal(side: EdgeSide): boolean {
  return side === "left" || side === "right";
}

/** El punto del borde donde se engancha una arista. */
export function anchorPoint(rect: Rect, side: EdgeSide): Point {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  switch (side) {
    case "top":
      return { x: cx, y: rect.y };
    case "bottom":
      return { x: cx, y: rect.y + rect.height };
    case "left":
      return { x: rect.x, y: cy };
    case "right":
      return { x: rect.x + rect.width, y: cy };
  }
}

/**
 * El par de lados por el que conviene unir dos nodos, según dónde están uno
 * respecto del otro.
 *
 * Es un cálculo de UNA vez (al crear o duplicar): el resultado se guarda
 * explícito en la arista y después no se recalcula. Nada lo llama por frame
 * para mover lados solos.
 */
export function autoSides(a: Rect, b: Rect): [EdgeSide, EdgeSide] {
  const dx = b.x + b.width / 2 - (a.x + a.width / 2);
  const dy = b.y + b.height / 2 - (a.y + a.height / 2);
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);

  // Sin historia: nodos concéntricos caen en horizontal, que es el default.
  const horizontal = ax >= ay;

  if (horizontal) return dx >= 0 ? ["right", "left"] : ["left", "right"];
  return dy >= 0 ? ["bottom", "top"] : ["top", "bottom"];
}

/** Agrega un punto salvo que repita el anterior (evita segmentos de largo 0). */
function push(out: number[], x: number, y: number): void {
  const n = out.length;
  if (n >= 2 && Math.abs(out[n - 2] - x) < 0.01 && Math.abs(out[n - 1] - y) < 0.01) return;
  out.push(x, y);
}

/**
 * Un carril por fuera de los dos nodos en el eje dado, del lado al que apunta
 * `dir` (+1 / −1). Es por donde pasa el rodeo cuando la arista sale y entra por
 * el mismo lado.
 */
function outerLane(a0: number, a1: number, b0: number, b1: number, dir: number): number {
  return dir > 0 ? Math.max(a1, b1) + STUB : Math.min(a0, b0) - STUB;
}

/**
 * Un carril libre entre dos intervalos: el hueco que dejan si no se solapan, y
 * si se solapan, apenas por fuera de la unión — por el borde más cercano a
 * `prefer`, para que el rodeo sea el corto.
 */
function clearLane(a0: number, a1: number, b0: number, b1: number, prefer: number): number {
  if (a1 < b0) return (a1 + b0) / 2;
  if (b1 < a0) return (b1 + a0) / 2;
  const before = Math.min(a0, b0) - STUB;
  const after = Math.max(a1, b1) + STUB;
  return Math.abs(prefer - before) <= Math.abs(prefer - after) ? before : after;
}

/** Ver `elbowCost`. */
const SCRATCH: number[] = [];

/**
 * ¿Corta el segmento (siempre horizontal o vertical) al rectángulo? Se encoge
 * el rect un pelo: una línea que corre pegada al borde roza pero no cruza.
 */
function segHitsRect(x0: number, y0: number, x1: number, y1: number, r: Rect): boolean {
  const inset = 0.5;
  const rx0 = r.x + inset;
  const ry0 = r.y + inset;
  const rx1 = r.x + r.width - inset;
  const ry1 = r.y + r.height - inset;
  return (
    Math.min(x0, x1) < rx1 &&
    Math.max(x0, x1) > rx0 &&
    Math.min(y0, y1) < ry1 &&
    Math.max(y0, y1) > ry0
  );
}

/**
 * Qué tan mal está un codo candidato. Manda no meterse adentro de un nodo;
 * después, no arrancar para el lado contrario al que apunta la salida (queda
 * como un rulo aunque no cruce nada); y recién al final, ser corto y con pocos
 * vértices. Los pesos están separados por órdenes de magnitud a propósito: la
 * comparación es jerárquica, no una suma de cosas parecidas.
 */
function elbowCost(
  p0: Point,
  p1: Point,
  mid: number[],
  p2: Point,
  p3: Point,
  n0: Point,
  n1: Point,
  a: Rect,
  b: Rect,
): number {
  // Scratch reusado: `routePath` corre en cada frame de un arrastre y esto se
  // llama una vez por candidato. Armar el array acá adentro era basura pura.
  const pts = SCRATCH;
  pts.length = 0;
  pts.push(p0.x, p0.y, p1.x, p1.y);
  for (let i = 0; i < mid.length; i++) pts.push(mid[i]);
  pts.push(p2.x, p2.y, p3.x, p3.y);
  let cost = 0;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    if (segHitsRect(pts[i], pts[i + 1], pts[i + 2], pts[i + 3], a)) cost += 1e6;
    if (segHitsRect(pts[i], pts[i + 1], pts[i + 2], pts[i + 3], b)) cost += 1e6;
    cost += Math.hypot(pts[i + 2] - pts[i], pts[i + 3] - pts[i + 1]);
  }
  cost += (pts.length / 2 - 2) * 20;
  // Retroceso: el primer tramo después del stub va contra la normal de salida
  // (ídem el último contra la de llegada).
  const f = { x: mid[0] - p1.x, y: mid[1] - p1.y };
  if (f.x * n0.x + f.y * n0.y < -0.01) cost += 1e4;
  const l = { x: mid[mid.length - 2] - p2.x, y: mid[mid.length - 1] - p2.y };
  if (l.x * n1.x + l.y * n1.y < -0.01) cost += 1e4;
  return cost;
}

/**
 * El camino de una arista, como polilínea plana [x0, y0, x1, y1, …] en
 * coordenadas de mundo. Las curvas vienen ya muestreadas, así que quien pinta,
 * mide o hace hit-test trabaja siempre contra el mismo formato.
 */
export function routePath(
  a: Rect,
  sideA: EdgeSide,
  b: Rect,
  sideB: EdgeSide,
  route: EdgeRoute,
): number[] {
  const p0 = anchorPoint(a, sideA);
  const p3 = anchorPoint(b, sideB);

  if (route === "straight") return [p0.x, p0.y, p3.x, p3.y];

  const n0 = SIDE_NORMAL[sideA];
  const n1 = SIDE_NORMAL[sideB];

  if (route === "curved") {
    const dx = p3.x - p0.x;
    const dy = p3.y - p0.y;
    const dist = Math.hypot(dx, dy);
    // La tangente nunca pasa de la mitad de la distancia: con el mínimo fijo,
    // dos nodos pegados daban tangentes más largas que el tramo y la curva se
    // enroscaba sobre sí misma.
    const base = Math.max(Math.min(dist * 0.4, CURVE_MAX), Math.min(CURVE_MIN, dist * 0.5));
    // Si el otro extremo queda DETRÁS de la salida, la tangente se alarga en
    // proporción a cuánto: es lo que hace que la curva rodee en vez de cruzar
    // el nodo del que acaba de salir.
    const backA = Math.max(0, -(n0.x * dx + n0.y * dy));
    const backB = Math.max(0, n1.x * dx + n1.y * dy);
    const kA = Math.min(base + backA * 0.9, CURVE_MAX_BACK);
    const kB = Math.min(base + backB * 0.9, CURVE_MAX_BACK);
    // Caso degenerado: lados enfrentados Y eje paralelo a las normales (un
    // right→left con el destino justo atrás). Ahí las dos tangentes caen sobre
    // la MISMA recta, así que alargarlas no separa nada y la curva vuelve
    // aplastada sobre los nodos. Se corren los dos controles hacia el mismo
    // lado, perpendicular al eje, y el camino rodea en C. `collapse` mide
    // cuánto se da esa condición: en cualquier otro ángulo vale 0 y no se toca
    // nada, porque ahí la tangente larga ya alcanza y el arco ensuciaría.
    // `collapse` vale 1 cuando LAS DOS normales son paralelas al eje que une
    // los extremos — que es exactamente cuando todo (anclas y controles) cae
    // sobre una misma recta. Cuanto más se aparta alguna, menos arco hace falta.
    const ux = dist > 1e-6 ? dx / dist : 0;
    const uy = dist > 1e-6 ? dy / dist : 0;
    const collapse =
      (1 - Math.abs(n0.x * uy - n0.y * ux)) * (1 - Math.abs(n1.x * uy - n1.y * ux));
    const bow = dist > 1e-6 ? { x: -dy / dist, y: dx / dist } : { x: -n0.y, y: n0.x };
    const bowA = collapse * Math.min(backA * 0.6, CURVE_MAX);
    const bowB = collapse * Math.min(backB * 0.6, CURVE_MAX);
    const c0 = { x: p0.x + n0.x * kA + bow.x * bowA, y: p0.y + n0.y * kA + bow.y * bowA };
    const c1 = { x: p3.x + n1.x * kB + bow.x * bowB, y: p3.y + n1.y * kB + bow.y * bowB };
    const out: number[] = [];
    for (let i = 0; i <= CURVE_SAMPLES; i++) {
      const t = i / CURVE_SAMPLES;
      const u = 1 - t;
      const w0 = u * u * u;
      const w1 = 3 * u * u * t;
      const w2 = 3 * u * t * t;
      const w3 = t * t * t;
      push(
        out,
        w0 * p0.x + w1 * c0.x + w2 * c1.x + w3 * p3.x,
        w0 * p0.y + w1 * c0.y + w2 * c1.y + w3 * p3.y,
      );
    }
    return out;
  }

  // Codo. En vez de una cascada de casos particulares, se arman los codos
  // POSIBLES para este par de lados y se elige el mejor con `elbowCost`: es el
  // mismo criterio para las 16 combinaciones de lados, así que no hay una
  // esquina del espacio de casos que quede sin cubrir por olvido.
  const p1 = { x: p0.x + n0.x * STUB, y: p0.y + n0.y * STUB };
  const p2 = { x: p3.x + n1.x * STUB, y: p3.y + n1.y * STUB };
  const aH = isHorizontal(sideA);
  const bH = isHorizontal(sideB);

  const mids: number[][] = [];
  if (aH === bH) {
    // Misma orientación: se cruza por un carril, y el carril puede ir sobre el
    // eje del recorrido (el corte al medio de toda la vida) o rodeando por
    // fuera de los dos nodos.
    const cross = aH
      ? [
          (p1.x + p2.x) / 2,
          outerLane(a.x, a.x + a.width, b.x, b.x + b.width, 1),
          outerLane(a.x, a.x + a.width, b.x, b.x + b.width, -1),
        ]
      : [
          (p1.y + p2.y) / 2,
          outerLane(a.y, a.y + a.height, b.y, b.y + b.height, 1),
          outerLane(a.y, a.y + a.height, b.y, b.y + b.height, -1),
        ];
    if (aH) {
      for (const x of cross) mids.push([x, p1.y, x, p2.y]);
      // …y el rodeo por el otro eje, que es lo que salva al destino que quedó
      // detrás de la salida: cortar al medio ahí devuelve la línea sobre sus
      // propios nodos.
      const y = clearLane(a.y, a.y + a.height, b.y, b.y + b.height, (p1.y + p2.y) / 2);
      mids.push([p1.x, y, p2.x, y]);
    } else {
      for (const y of cross) mids.push([p1.x, y, p2.x, y]);
      const x = clearLane(a.x, a.x + a.width, b.x, b.x + b.width, (p1.x + p2.x) / 2);
      mids.push([x, p1.y, x, p2.y]);
    }
  } else {
    // Orientaciones cruzadas: alcanza con un vértice, y hay dos formas de
    // ponerlo — doblar al final o doblar apenas se sale.
    mids.push([p2.x, p1.y]);
    mids.push([p1.x, p2.y]);
    // …y los rodeos por fuera, para cuando las dos formas simples chocan. Van
    // los dos ejes: cuál sirve depende de por dónde quedó el nodo de destino.
    const x = outerLane(a.x, a.x + a.width, b.x, b.x + b.width, aH ? n0.x : n1.x);
    const y = outerLane(a.y, a.y + a.height, b.y, b.y + b.height, aH ? n1.y : n0.y);
    if (aH) {
      mids.push([x, p1.y, x, p2.y]);
      mids.push([p1.x, y, p2.x, y]);
    } else {
      mids.push([p1.x, y, p2.x, y]);
      mids.push([x, p1.y, x, p2.y]);
    }
  }

  let best = mids[0];
  let bestCost = Infinity;
  for (const mid of mids) {
    const cost = elbowCost(p0, p1, mid, p2, p3, n0, n1, a, b);
    if (cost < bestCost) {
      bestCost = cost;
      best = mid;
    }
  }

  const out: number[] = [];
  push(out, p0.x, p0.y);
  push(out, p1.x, p1.y);
  for (let i = 0; i + 1 < best.length; i += 2) push(out, best[i], best[i + 1]);
  push(out, p2.x, p2.y);
  push(out, p3.x, p3.y);
  return out;
}

/** Unitario, o el fallback si el vector es (casi) nulo. */
function normalize(x: number, y: number): Point {
  const len = Math.hypot(x, y);
  if (len < 1e-6) return { x: 1, y: 0 };
  return { x: x / len, y: y / len };
}

/**
 * Dirección a la que apunta la punta de flecha del extremo final: la del
 * último tramo con largo. Se camina hacia atrás porque una ruta puede terminar
 * en dos puntos casi pegados (nodos superpuestos).
 */
export function endDirection(path: number[]): Point {
  const n = path.length;
  if (n < 4) return { x: 1, y: 0 };
  const ex = path[n - 2];
  const ey = path[n - 1];
  for (let i = n - 4; i >= 0; i -= 2) {
    const dx = ex - path[i];
    const dy = ey - path[i + 1];
    if (Math.hypot(dx, dy) > 1e-6) return normalize(dx, dy);
  }
  return { x: 1, y: 0 };
}

/** Ídem para el extremo inicial: apunta hacia afuera del camino. */
export function startDirection(path: number[]): Point {
  for (let i = 2; i < path.length; i += 2) {
    const dx = path[0] - path[i];
    const dy = path[1] - path[i + 1];
    if (Math.hypot(dx, dy) > 1e-6) return normalize(dx, dy);
  }
  return { x: -1, y: 0 };
}

/**
 * Triángulo de la punta de flecha: [x0,y0, x1,y1, x2,y2], con el vértice en
 * `tip` mirando hacia `dir` (unitario).
 */
export function arrowHead(tip: Point, dir: Point, size: number): number[] {
  const bx = tip.x - dir.x * size;
  const by = tip.y - dir.y * size;
  const px = -dir.y * size * 0.45;
  const py = dir.x * size * 0.45;
  return [tip.x, tip.y, bx + px, by + py, bx - px, by - py];
}

/** Distancia de un punto al segmento (ax,ay)-(bx,by). */
function distanceToSegment(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  px: number,
  py: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-9) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/** Distancia del punto a la polilínea. Es el hit-test de una arista. */
export function distanceToPath(path: number[], px: number, py: number): number {
  if (path.length < 4) {
    return path.length === 2 ? Math.hypot(px - path[0], py - path[1]) : Infinity;
  }
  let best = Infinity;
  for (let i = 0; i + 3 < path.length; i += 2) {
    const d = distanceToSegment(path[i], path[i + 1], path[i + 2], path[i + 3], px, py);
    if (d < best) best = d;
  }
  return best;
}

/** Largo total de la polilínea. */
export function pathLength(path: number[]): number {
  let total = 0;
  for (let i = 0; i + 3 < path.length; i += 2) {
    total += Math.hypot(path[i + 2] - path[i], path[i + 3] - path[i + 1]);
  }
  return total;
}

/**
 * El punto que está a la fracción `t` (0..1) del LARGO del camino.
 *
 * Se mide por largo y no por índice de vértice: en un codo los tramos son de
 * tamaños muy distintos, así que el vértice del medio casi nunca cae en el medio
 * visual. La etiqueta de una arista se ancla acá.
 */
export function pointAtFraction(path: number[], t: number): Point {
  if (path.length < 4) return { x: path[0] ?? 0, y: path[1] ?? 0 };
  const target = pathLength(path) * Math.min(Math.max(t, 0), 1);
  let walked = 0;
  for (let i = 0; i + 3 < path.length; i += 2) {
    const len = Math.hypot(path[i + 2] - path[i], path[i + 3] - path[i + 1]);
    if (walked + len >= target && len > 1e-9) {
      const k = (target - walked) / len;
      return {
        x: path[i] + (path[i + 2] - path[i]) * k,
        y: path[i + 1] + (path[i + 3] - path[i + 1]) * k,
      };
    }
    walked += len;
  }
  return { x: path[path.length - 2], y: path[path.length - 1] };
}

/** Caja que envuelve a la polilínea. */
export function pathBounds(path: number[]): Rect {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i + 1 < path.length; i += 2) {
    if (path[i] < minX) minX = path[i];
    if (path[i] > maxX) maxX = path[i];
    if (path[i + 1] < minY) minY = path[i + 1];
    if (path[i + 1] > maxY) maxY = path[i + 1];
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * Caja donde con certeza cae la ruta entre dos nodos, sin calcularla.
 *
 * Es lo que permite descartar una arista fuera de pantalla barato. Ojo con la
 * trampa: una arista puede ser visible con **los dos extremos fuera** de la
 * pantalla, así que lo que se testea es esta caja, nunca los rects de los nodos.
 */
export function routeCullBounds(a: Rect, b: Rect): Rect {
  const minX = Math.min(a.x, b.x) - ROUTE_MARGIN;
  const minY = Math.min(a.y, b.y) - ROUTE_MARGIN;
  const maxX = Math.max(a.x + a.width, b.x + b.width) + ROUTE_MARGIN;
  const maxY = Math.max(a.y + a.height, b.y + b.height) + ROUTE_MARGIN;
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

export function rectsIntersect(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y
  );
}

/** Rectángulo agrandado por `margin` en los cuatro lados. */
export function expandRect(r: Rect, margin: number): Rect {
  return {
    x: r.x - margin,
    y: r.y - margin,
    width: r.width + margin * 2,
    height: r.height + margin * 2,
  };
}

/** ¿`inner` cae entero adentro de `outer`? */
export function rectContains(outer: Rect, inner: Rect): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.width <= outer.x + outer.width &&
    inner.y + inner.height <= outer.y + outer.height
  );
}

/**
 * Banda de zoom: el zoom redondeado a pasos geométricos del 10%.
 *
 * El grosor de una arista se compensa dividiendo por el zoom, para que se vea
 * de 1 px de pantalla en cualquier acercamiento. Recalcularlo con el zoom exacto
 * obligaría a repintar en cada frame de un gesto de zoom. Con bandas se repinta
 * sólo al cruzar una, y el error de grosor adentro de la banda es ≤5%: invisible
 * en una línea de 1 px, y el mismo criterio escalonado que ya usan los tiers de
 * textura en `lod.ts`.
 */
export function zoomBand(zoom: number): number {
  return Math.round(Math.log(zoom) / Math.log(1.1));
}

/** El zoom nominal de una banda. Se pinta con éste y no con el exacto, para que
 *  el grosor sea estable adentro de la banda en vez de saltar en cada cruce. */
export function bandZoom(band: number): number {
  return Math.pow(1.1, band);
}
